import { Box, Card, CardMedia, Chip, alpha, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { Verified, Public, WorkspacePremium } from "@mui/icons-material";
import { useState } from "react";

const HeroImage = () => {
  const [isFlipped, setIsFlipped] = useState(false);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: { xs: "center", md: "flex-start" },
        alignItems: "center",
        width: "100%",
        height: "100%",
        pl: { md: 4 },
        pr: { md: 2 },
      }}
    >
      {/* Enhanced animated background glow */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          background: "radial-gradient(circle, rgba(200,169,107,0.12) 0%, rgba(200,169,107,0.02) 50%, transparent 80%)",
          borderRadius: "50%",
          filter: "blur(20px)",
          left: "0",
          top: "50%",
          transform: "translateY(-50%)",
        }}
      />
      
      {/* Rotating ring decoration */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "absolute",
          width: "115%",
          height: "115%",
          borderRadius: "50%",
          border: "1px dashed rgba(200,169,107,0.12)",
          pointerEvents: "none",
          left: "-7.5%",
          top: "50%",
          transform: "translateY(-50%)",
        }}
      />
      
      <motion.div
        initial={{ scale: 0.9, opacity: 0, x: -20 }}
        animate={{ scale: 1, opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.4, type: "spring", stiffness: 120 }}
        style={{
          width: "100%",
          maxWidth: "500px",
          margin: { xs: "0 auto", md: "0" },
        }}
      >
        <Card
          elevation={0}
          sx={{
            position: "relative",
            width: "100%",
            background: "linear-gradient(135deg, rgba(26,26,26,0.95), rgba(11,11,11,0.98))",
            backdropFilter: "blur(10px)",
            borderRadius: "28px",
            overflow: "hidden",
            border: "1px solid rgba(200,169,107,0.3)",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            cursor: "pointer",
            "&:hover": {
              border: "1px solid rgba(200,169,107,0.6)",
              transform: "translateY(-4px)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
            },
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "2px",
              background: "linear-gradient(90deg, transparent, #C8A96B, #C8A96B, transparent)",
              opacity: 0.7,
              zIndex: 2,
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 3.5,
            }}
          >
            {/* Professional Title Badge Above Image */}
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.3 }}
              style={{ marginBottom: 24 }}
            >
              <Chip
                icon={<WorkspacePremium sx={{ color: "#C8A96B", fontSize: 16 }} />}
                label="John Asfar"
                size="medium"
                sx={{
                  background: alpha("#C8A96B", 0.1),
                  backdropFilter: "blur(10px)",
                  color: "#C8A96B",
                  border: "1px solid rgba(200,169,107,0.35)",
                  fontWeight: 600,
                  height: 34,
                  fontSize: "0.8rem",
                  "& .MuiChip-label": {
                    px: 2.5,
                    letterSpacing: "0.3px",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                  },
                }}
              />
            </motion.div>

            {/* Flip Container - Same space for both image and logo */}
            <Box
              sx={{
                perspective: "1000px",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                mb: 2.5,
              }}
            >
              <motion.div
                onClick={() => setIsFlipped(!isFlipped)}
                style={{
                  width: "300px",
                  height: "300px",
                  position: "relative",
                  cursor: "pointer",
                  transformStyle: "preserve-3d",
                }}
                animate={{ rotateY: isFlipped ? 180 : 0 }}
                transition={{ 
                  duration: 0.6, 
                  type: "spring", 
                  stiffness: 80,
                  damping: 15
                }}
              >
                {/* Front Side - Person's Image */}
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    backfaceVisibility: "hidden",
                    borderRadius: "24px",
                    overflow: "hidden",
                    border: "3px solid rgba(200,169,107,0.4)",
                    boxShadow: "0 12px 24px rgba(0,0,0,0.4), 0 0 0 4px rgba(200,169,107,0.05)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      border: "3px solid #C8A96B",
                      boxShadow: "0 16px 32px rgba(0,0,0,0.5), 0 0 0 6px rgba(200,169,107,0.08)",
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    image="/images/jp.jpeg"
                    alt="John Asfar"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                  />
                  {/* Gold inner border accent */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: "24px",
                      border: "1px solid rgba(200,169,107,0.2)",
                      pointerEvents: "none",
                    }}
                  />
                  {/* Flip instruction overlay */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      right: 16,
                      background: alpha("#000", 0.6),
                      backdropFilter: "blur(4px)",
                      borderRadius: "20px",
                      padding: "6px 12px",
                      pointerEvents: "none",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#C8A96B",
                        fontSize: "0.7rem",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Click to flip →
                    </Typography>
                  </Box>
                </Box>

                {/* Back Side - Logo */}
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    backfaceVisibility: "hidden",
                    transform: "rotateY(180deg)",
                    background: "linear-gradient(135deg, rgba(200,169,107,0.1), rgba(200,169,107,0.03))",
                    borderRadius: "24px",
                    border: "3px solid rgba(200,169,107,0.4)",
                    boxShadow: "0 12px 24px rgba(0,0,0,0.4), 0 0 0 4px rgba(200,169,107,0.05)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 3,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      border: "3px solid #C8A96B",
                      boxShadow: "0 16px 32px rgba(0,0,0,0.5), 0 0 0 6px rgba(200,169,107,0.08)",
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    image="/images/logo.png"
                    alt="Company Logo"
                    sx={{
                      width: "75%",
                      height: "auto",
                      objectFit: "contain",
                      mb: 2,
                    }}
                  />
                  
                  <Box
                    sx={{
                      width: "45px",
                      height: "2px",
                      background: "linear-gradient(90deg, #C8A96B, transparent)",
                      margin: "10px auto",
                    }}
                  />
                  
                  <Typography
                    sx={{
                      color: alpha("#C8A96B", 0.8),
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      textAlign: "center",
                    }}
                  >
                    Global Network
                  </Typography>

                  {/* Flip instruction on back */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      right: 16,
                      background: alpha("#000", 0.6),
                      backdropFilter: "blur(4px)",
                      borderRadius: "20px",
                      padding: "6px 12px",
                      pointerEvents: "none",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#C8A96B",
                        fontSize: "0.7rem",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Click to flip ←
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Box>

            {/* Flip Hint Text */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.5 }}
            >
              <Typography
                sx={{
                  color: alpha("#FFFFFF", 0.45),
                  fontSize: "0.7rem",
                  textAlign: "center",
                  letterSpacing: "0.5px",
                }}
              >
                Click the card to flip
              </Typography>
            </motion.div>
            
            {/* Subtle gradient overlay */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: "radial-gradient(circle at 50% 30%, rgba(200,169,107,0.03), transparent 70%)",
                pointerEvents: "none",
              }}
            />
          </Box>
        </Card>
      </motion.div>

      {/* Floating Badges - Repositioned for left layout with more space */}
      <motion.div
        initial={{ opacity: 0, x: -15, y: 10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 0.9, duration: 0.4, type: "spring", stiffness: 200 }}
        style={{
          position: "absolute",
          top: "-8%",
          left: "-15px",
          zIndex: 10,
        }}
      >
        <Chip
          icon={<Verified sx={{ color: "#C8A96B", fontSize: 16 }} />}
          label="Executive Leader"
          size="medium"
          sx={{
            background: alpha("#0B0B0B", 0.95),
            backdropFilter: "blur(10px)",
            color: "#C8A96B",
            border: "1px solid rgba(200,169,107,0.4)",
            fontWeight: 600,
            height: 34,
            "& .MuiChip-label": {
              px: 2,
              fontSize: "0.75rem",
              fontWeight: 600,
              letterSpacing: "0.2px",
            },
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
          }}
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 15, y: -10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 1, duration: 0.4, type: "spring", stiffness: 200 }}
        style={{
          position: "absolute",
          bottom: "-5%",
          right: "-15px",
          zIndex: 10,
        }}
      >
        <Chip
          icon={<Public sx={{ color: "#C8A96B", fontSize: 16 }} />}
          label="Global Influence"
          size="medium"
          sx={{
            background: alpha("#0B0B0B", 0.95),
            backdropFilter: "blur(10px)",
            color: "#C8A96B",
            border: "1px solid rgba(200,169,107,0.4)",
            fontWeight: 600,
            height: 34,
            "& .MuiChip-label": {
              px: 2,
              fontSize: "0.75rem",
              fontWeight: 600,
              letterSpacing: "0.2px",
            },
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
          }}
        />
      </motion.div>
    </Box>
  );
};

export default HeroImage;